/**
 * MTA schedule and realtime epochs describe New York service, so rider-facing clocks use
 * New York time even when the device is elsewhere. Keeping the zone explicit also makes the
 * spring-forward gap and repeated fall-back hour follow the service clock rather than the
 * device's current zone.
 */
export const MTA_TIME_ZONE = 'America/New_York'

/**
 * The ZONE is fixed to New York; the LOCALE deliberately is not. Every formatter these
 * replaced passed `[]` to `toLocaleTimeString`, i.e. the device's own locale, so a phone set
 * to 24-hour time showed 20:05 like the rest of its OS. Hardcoding 'en-US' here would print
 * 8:05 PM on that phone in the station sheet, the bus stop sheet, the trip timetable and the
 * riding panel's sign. `undefined` means "the runtime's locale"; tests pass one explicitly so
 * they do not depend on the machine running them.
 */
type ClockLocales = string | string[] | undefined

interface ClockOptions {
  fallback?: string
  /** Override the device locale (deterministic tests, or a caller with its own preference). */
  locales?: ClockLocales
}

interface CountdownOptions {
  fallback?: string
  includeUnit?: boolean
  nowLabel?: string
  pastLabel?: string
  unit?: string
  /** Injectable clock for deterministic callers and tests; Unix seconds. */
  nowSeconds?: number
}

// Constructing an Intl.DateTimeFormat is the expensive part, and these run per arrival row,
// so keep one per (hour style, locale) rather than one per call.
const clockCache = new Map<string, Intl.DateTimeFormat>()

function clock(hour: '2-digit' | 'numeric', locales: ClockLocales): Intl.DateTimeFormat {
  const key = `${hour}|${locales == null ? '' : String(locales)}`
  let formatter = clockCache.get(key)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locales, {
      hour,
      minute: '2-digit',
      timeZone: MTA_TIME_ZONE,
    })
    clockCache.set(key, formatter)
  }
  return formatter
}

/** Clock time with a two-digit hour, for displays where columns must stay aligned. */
export function formatAbsoluteClock(
  epochSeconds: number | null,
  { fallback = '--', locales }: ClockOptions = {},
): string {
  return epochSeconds == null ? fallback : clock('2-digit', locales).format(epochSeconds * 1000)
}

/** Natural clock time without a leading hour zero. */
export function formatShortClock(
  epochSeconds: number | null,
  { fallback = '--', locales }: ClockOptions = {},
): string {
  return epochSeconds == null ? fallback : clock('numeric', locales).format(epochSeconds * 1000)
}

/**
 * Nearest-minute countdown. Current-minute and past arrivals are clamped to their labels;
 * callers choose casing, unit text, and whether the unit belongs in this string.
 */
export function formatCountdown(
  epochSeconds: number | null,
  {
    fallback = '',
    includeUnit = true,
    nowLabel = 'Now',
    pastLabel = nowLabel,
    unit = 'min',
    nowSeconds = Date.now() / 1000,
  }: CountdownOptions = {},
): string {
  if (epochSeconds == null) return fallback
  const minutes = Math.round((epochSeconds - nowSeconds) / 60)
  if (minutes < 0) return pastLabel
  if (minutes === 0) return nowLabel
  return includeUnit ? `${minutes} ${unit}` : `${minutes}`
}
